/// <amd-module name="TFS/Admin/Common/Util/Telemetry/AdminTelemetry" />
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IVssPageContext } from "VSS/Platform/Context";
import { Area, Feature, TelemetryProperties } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { TelemetrySanitizer } from "TFS/Admin/Common/Util/Telemetry/TelemetrySanitizer";

export interface AdminTelemetry {
    context: IVssPageContext;
    sendTelemetry(area: string, feature: string, action: string, errorMessage?: string): void;
    sendComplexTelemetry(area: string, feature: string, properties: { [key: string]: any }): void;
    sendDataProviderTelemetry(feature: string, dataProvider: string, result: boolean, errorMessage?: string): void;
}

export class AdminTelemetry implements AdminTelemetry {
    private _telemetrySanitizer: TelemetrySanitizer;

    public constructor(context?: any) {
        this.context = context;
        this._telemetrySanitizer = new TelemetrySanitizer();
    }

    sendTelemetry = (area: string, feature: string, action: string, errorMessage?: string): void => {
        this.sendComplexTelemetry(area, feature, {
            Action: action,
            ErrorMessage: errorMessage ? this._telemetrySanitizer.CleanErrorMessage(errorMessage) : ""
        });
    };

    sendComplexTelemetry = (area: string, feature: string, properties: { [key: string]: any }): void => {
        const telemetryService = this.context.getService<IVssTelemetryService>("IVssTelemetryService");

        let errorMessage = properties[TelemetryProperties.ErrorMessage];
        if (errorMessage) {
            properties[TelemetryProperties.ErrorMessage] = this._telemetrySanitizer.CleanErrorMessage(errorMessage);
        }

        telemetryService.publishEvent(area, feature, properties);
    };

    sendDataProviderTelemetry = (feature: string, dataProvider: string, result: boolean, errorMessage?: string): void => {
        this.sendComplexTelemetry(Area.Admin, feature, {
            DataProvider: dataProvider,
            Result: result,
            ErrorMessage: this._telemetrySanitizer.CleanErrorMessage(errorMessage)
        });
    };

    startActionCapture = (feature: string, subFeature: string, action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        if (!properties) {
            properties = {};
        }

        properties[TelemetryProperties.Action] = action;
        this.sendComplexTelemetry(Area.Admin, feature, properties);
        return new AdminFeatureTelemetryLogger(this.context, feature, subFeature ? subFeature : feature, action);
    };
}

export class AdminFeatureTelemetryLogger {
    protected _adminTelemetry: AdminTelemetry;
    private _pageContext: IVssPageContext;
    private _feature: string;
    private _subFeature: string;
    private _action: string;

    public constructor(context: IVssPageContext, feature: string, subFeature: string, action: string) {
        this._pageContext = context;
        this._adminTelemetry = new AdminTelemetry(context);

        this._feature = feature;
        this._subFeature = subFeature;
        this._action = action;
    }

    public captureAttemptStart = (): void => {
        this.captureTelemetry({});
    };

    public captureError = (errorMessage: string): void => {
        var properties: { [key: string]: any } = {};
        properties[TelemetryProperties.Action] = this._action;
        properties[TelemetryProperties.SubFeature] = this._subFeature;
        properties[TelemetryProperties.ErrorMessage] = errorMessage;
        properties[TelemetryProperties.PageUrl] = window.location.pathname.toLowerCase();

        this._adminTelemetry.sendComplexTelemetry(Area.Admin, this._feature, properties);
    };

    public captureTelemetry = (properties: { [key: string]: any }): void => {
        properties[TelemetryProperties.Action] = this._action;
        properties[TelemetryProperties.SubFeature] = this._subFeature;
        properties[TelemetryProperties.PageUrl] = window.location.pathname.toLowerCase();

        this._adminTelemetry.sendComplexTelemetry(Area.Admin, this._feature, properties);
    };

    public captureDataProviderCall = (dataProvider: string, result: boolean, errorMessage?: string): void => {
        this._adminTelemetry.sendDataProviderTelemetry(this._feature, dataProvider, result, errorMessage);
    };

    public getTelemetryLoggerForNewAction = (action: string): AdminFeatureTelemetryLogger => {
        return new AdminFeatureTelemetryLogger(this._pageContext, this._feature, this._subFeature, action);
    };
}
